import { Controller } from "@hotwired/stimulus";

/**
 * ポイント購入モーダルコントローラー
 *
 * 責任範囲:
 * - ポイント購入モーダルの表示・非表示制御
 * - 2画面（purchase/confirm）の切り替え制御
 * - 購入金額選択時の確認画面内容更新
 *
 * 使用シナリオ:
 * - シナリオ(1): ポイント・収穫金ともに不足
 */
export class PointPurchaseModalController extends Controller {
  static targets = [
    'modal',
    'amountRadio',
    'pointTermsCheckbox',
    'purchasePointValue',
    'confirmPurchasePoints',
    'confirmPurchasePointPrice'
  ]

  /**
   * モーダルを表示（常に購入画面から開始）
   * @param {Object} data - 表示データ
   * @param {number} data.insufficientPoints - 不足ポイント額
   */
  show(data) {
    // data-screen="purchase"に初期化（常に購入画面から開始）
    this.modalTarget.dataset.screen = 'purchase';

    // 不足ポイント額を保存（確認画面で使用）
    this.insufficientPoints = data.insufficientPoints;

    // ラジオボタンが既に選択されている場合、強制的にonAmountChange()を実行
    // これにより、購入数量を変更した際にも確認画面の金額が正しく更新される
    const selectedRadio = this.amountRadioTargets.find(radio => radio.checked);
    if (selectedRadio) {
      this.onAmountChange();
    }

    // モーダルを表示
    this.modalTarget.style.display = 'block';
    this.modalTarget.style.opacity = 1;
  }

  /**
   * ポイント購入金額選択時の処理
   * トリガー: ポイント購入金額ラジオボタンのchangeイベント
   * HTML: _purchase_point_modal.html.erb:25
   *       data-action="change->point-purchase-modal#onAmountChange"
   * 動作: 選択された金額を確認画面に反映
   */
  onAmountChange() {
    let purchasePoint = 0;
    const selectedValue = this.amountRadioTargets.find(radio => radio.checked)?.value;

    if (selectedValue === 'insufficient') {
      purchasePoint = this.insufficientPoints;
    } else {
      purchasePoint = Number(selectedValue);
    }

    if (purchasePoint === '' || isNaN(purchasePoint)) { return }

    // 確認画面の表示内容を更新
    this.purchasePointValueTarget.value = purchasePoint;
    this.confirmPurchasePointsTarget.textContent = this.formatCurrency(this.insufficientPoints);
    this.confirmPurchasePointPriceTarget.textContent = this.formatCurrency(purchasePoint);
  }

  /**
   * ポイント購入確認画面へ遷移
   * トリガー: ポイント購入画面のフォームsubmitイベント
   * HTML: _purchase_point_modal.html.erb:12
   *       data: { action: 'submit->point-purchase-modal#goToConfirmScreen' }
   * 動作: data-screen属性を"confirm"に変更して確認画面を表示
   */
  goToConfirmScreen(event) {
    event.preventDefault();

    // バリデーション
    if (!this.amountRadioTargets.find(radio => radio.checked)) {
      return;
    }
    if (!this.pointTermsCheckboxTarget.checked) {
      return;
    }

    // data-screen="confirm"に設定（CSSで自動的に画面切り替え）
    this.modalTarget.dataset.screen = 'confirm';
  }

  /**
   * ポイント購入画面へ戻る
   * トリガー: ポイント購入確認画面の「戻る」ボタンclickイベント
   * HTML: _purchase_point_modal.html.erb:94
   *       data-action="point-purchase-modal#goToPurchaseScreen"
   * 動作: data-screen属性を"purchase"に変更して購入画面を表示
   */
  goToPurchaseScreen(event) {
    event.preventDefault();
    // data-screen="purchase"に設定（CSSで自動的に画面切り替え）
    this.modalTarget.dataset.screen = 'purchase';
  }

  /**
   * モーダルを閉じる
   * トリガー: 「戻る」ボタンclickイベント
   * HTML: _purchase_point_modal.html.erb:46
   *       data-action="point-purchase-modal#close"
   */
  close(event) {
    event.preventDefault();
    this.modalTarget.style.display = 'none';
    this.modalTarget.style.opacity = 0;
  }

  /**
   * 数値をカンマ区切りにフォーマット
   * @param {number} value - フォーマットする数値
   * @returns {string} カンマ区切りの文字列
   */
  formatCurrency(value) {
    return value.toLocaleString('en-US');
  }
}
